import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import {
  Search,
  RotateCcw,
  Package,
  Boxes,
  IndianRupee,
  Layers,
  AlertTriangle,
  Eye,
  ShoppingCart,
  CheckCircle2,
  XCircle,
  Tag,
  Barcode,
  ExternalLink,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StandardImage,
  StatsGrid,
  type DataTableColumn,
  type DataTablePaginationMeta,
} from "@/components/shared";
import { channelStockService, type ChannelStockMine } from "@/services/channelStockService";
import { categoriesService } from "@/services/categoriesService";
import { useChannelDesk } from "@/lib/channel-desk";

export default function DistributorStock() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const desk = useChannelDesk();
  const catalogueUrl = desk.base === "/store/supply" ? "/store/supply" : `${desk.base}/catalogue`;

  // Determine effective user role across URL pathname, desk context, and auth context
  const userRoleType = useMemo<"store" | "wholesaler" | "distributor">(() => {
    if (
      pathname.includes("/store") ||
      desk.base === "/store/supply" ||
      desk.role === "Retailer" ||
      user?.role === "store"
    ) {
      return "store";
    }
    if (
      pathname.includes("/wholesaler") ||
      desk.base === "/wholesaler" ||
      desk.role === "Wholesaler" ||
      user?.role === "wholesaler"
    ) {
      return "wholesaler";
    }
    return "distributor";
  }, [pathname, desk.base, desk.role, user?.role]);

  // Helper to compute role-based pricing information
  const getRolePricingDetails = (product: ChannelStockMine) => {
    if (userRoleType === "store") {
      const ownBuyPrice = product.retailPrice > 0 ? product.retailPrice : (product.sellingPrice || product.price);
      const sellToCustomerPrice = product.sellingPrice > 0 ? product.sellingPrice : product.price;
      const margin = sellToCustomerPrice > ownBuyPrice ? sellToCustomerPrice - ownBuyPrice : 0;
      const marginPct = ownBuyPrice > 0 ? Math.round((margin / ownBuyPrice) * 100) : 0;

      return {
        roleBadge: "Store Partner",
        roleTitle: "Store Pricing",
        ownPriceLabel: "Store Own Price (RP)",
        ownPriceSubtext: "Price you pay to Wholesaler",
        ownPrice: ownBuyPrice,
        sellPriceLabel: "Customer Selling Price",
        sellPriceSubtext: "Price at which you sell to Customer",
        sellPrice: sellToCustomerPrice,
        tierTag: "RP",
        margin,
        marginPct,
        mrp: product.mrp,
        bv: product.businessVolume,
        stockBuyValue: ownBuyPrice * (product.quantity || 0),
        stockSellValue: sellToCustomerPrice * (product.quantity || 0),
      };
    }

    if (userRoleType === "wholesaler") {
      const ownBuyPrice = product.wholesalePrice > 0 ? product.wholesalePrice : product.price;
      const sellToStorePrice = product.retailPrice > 0 ? product.retailPrice : (product.sellingPrice || product.price);
      const margin = sellToStorePrice > ownBuyPrice ? sellToStorePrice - ownBuyPrice : 0;
      const marginPct = ownBuyPrice > 0 ? Math.round((margin / ownBuyPrice) * 100) : 0;

      return {
        roleBadge: "Wholesaler Partner",
        roleTitle: "Wholesaler Pricing",
        ownPriceLabel: "Wholesaler Own Price (WP)",
        ownPriceSubtext: "Price you pay to Distributor",
        ownPrice: ownBuyPrice,
        sellPriceLabel: "Selling Price to Store (RP)",
        sellPriceSubtext: "Price at which you sell to Store",
        sellPrice: sellToStorePrice,
        tierTag: "WP",
        margin,
        marginPct,
        mrp: product.mrp,
        bv: product.businessVolume,
        stockBuyValue: ownBuyPrice * (product.quantity || 0),
        stockSellValue: sellToStorePrice * (product.quantity || 0),
      };
    }

    // Default: Distributor
    const ownBuyPrice = product.distributorPrice > 0 ? product.distributorPrice : product.price;
    const sellToWholesalerPrice = product.wholesalePrice > 0 ? product.wholesalePrice : (product.retailPrice || product.price);
    const margin = sellToWholesalerPrice > ownBuyPrice ? sellToWholesalerPrice - ownBuyPrice : 0;
    const marginPct = ownBuyPrice > 0 ? Math.round((margin / ownBuyPrice) * 100) : 0;

    return {
      roleBadge: "Distributor Partner",
      roleTitle: "Distributor Pricing",
      ownPriceLabel: "Distributor Own Price (DP)",
      ownPriceSubtext: "Price you pay to Admin",
      ownPrice: ownBuyPrice,
      sellPriceLabel: "Selling Price to Wholesaler (WP)",
      sellPriceSubtext: "Price at which you sell to Wholesaler",
      sellPrice: sellToWholesalerPrice,
      tierTag: "DP",
      margin,
      marginPct,
      mrp: product.mrp,
      bv: product.businessVolume,
      stockBuyValue: ownBuyPrice * (product.quantity || 0),
      stockSellValue: sellToWholesalerPrice * (product.quantity || 0),
    };
  };

  // Search & filter states
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [stockStatusFilter, setStockStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Selected product for detail modal
  const [selectedProduct, setSelectedProduct] = useState<ChannelStockMine | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Stock query with backend filtering
  const stockQuery = useQuery({
    queryKey: [
      "channel",
      "stock",
      desk.base,
      appliedSearch,
      categoryFilter,
      stockStatusFilter,
    ],
    queryFn: () =>
      channelStockService.getMine({
        search: appliedSearch || undefined,
        category: categoryFilter !== "all" ? categoryFilter : undefined,
        stockStatus: stockStatusFilter !== "all" ? stockStatusFilter : undefined,
      }),
  });

  // Query categories for category filter
  const categoriesQuery = useQuery({
    queryKey: ["categories", "root"],
    queryFn: () => categoriesService.getRoot(),
    staleTime: 5 * 60 * 1000,
  });

  const rows = stockQuery.data ?? [];

  // Summary statistics calculated using role's actual purchase price
  const stats = useMemo(() => {
    const totalItems = rows.length;
    const totalUnits = rows.reduce((sum, r) => sum + (r.quantity ?? 0), 0);
    const totalStockValue = rows.reduce((sum, r) => {
      const pricing = getRolePricingDetails(r);
      return sum + pricing.ownPrice * (r.quantity || 0);
    }, 0);
    const totalBv = rows.reduce((sum, r) => sum + (r.businessVolume || 0) * (r.quantity || 0), 0);
    const lowStockCount = rows.filter(
      (r) => (r.quantity ?? 0) > 0 && (r.quantity ?? 0) <= (r.lowStockThreshold || 5),
    ).length;
    const outOfStockCount = rows.filter((r) => (r.quantity ?? 0) === 0).length;

    return {
      totalItems,
      totalUnits,
      totalStockValue,
      totalBv,
      lowStockCount,
      outOfStockCount,
    };
  }, [rows, userRoleType]);

  // Extract distinct categories available from products or category service
  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    (categoriesQuery.data ?? []).forEach((c) => {
      if (c.name) set.add(c.name);
    });
    rows.forEach((r) => {
      if (r.categoryName) set.add(r.categoryName);
    });
    return Array.from(set).sort();
  }, [categoriesQuery.data, rows]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [appliedSearch, categoryFilter, stockStatusFilter]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const paginationMeta = useMemo<DataTablePaginationMeta>(() => ({
    page,
    pageSize,
    totalCount: rows.length,
    totalPages: Math.ceil(rows.length / pageSize) || 1,
    from: rows.length === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, rows.length),
  }), [page, pageSize, rows.length]);

  // Handle Search Submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
  };

  // Handle Reset / Clear
  const handleResetFilters = () => {
    setSearchInput("");
    setAppliedSearch("");
    setCategoryFilter("all");
    setStockStatusFilter("all");
    setPage(1);
  };

  const isFiltered = Boolean(appliedSearch || categoryFilter !== "all" || stockStatusFilter !== "all");

  const openDetails = (product: ChannelStockMine) => {
    setSelectedProduct(product);
    setIsDetailOpen(true);
  };

  // Helper to format currency
  const formatInr = (amount: number) => `₹${Number(amount || 0).toLocaleString("en-IN")}`;

  // Helper for tier price label based on role
  const getTierPriceInfo = (item: ChannelStockMine) => {
    return getRolePricingDetails(item);
  };

  // Table Columns Definition
  const columns: DataTableColumn<ChannelStockMine>[] = [
    {
      id: "product",
      header: "Product",
      accessorKey: "productName",
      sortable: true,
      mobilePriority: 1,
      cell: (item) => (
        <div className="flex items-center gap-3 min-w-[200px]">
          <StandardImage
            src={item.imageUrl}
            alt={item.productName}
            standard="product"
            className="h-12 w-12 rounded-lg border object-cover shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
          />
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => openDetails(item)}
              className="font-semibold text-foreground hover:text-primary transition-colors text-left text-sm leading-snug line-clamp-2"
            >
              {item.productName}
            </button>
            <div className="flex flex-wrap items-center gap-1.5 mt-1 text-xs text-muted-foreground">
              {item.categoryName ? (
                <span className="inline-flex items-center gap-0.5 rounded bg-secondary px-1.5 py-0.5 font-medium text-foreground text-[11px]">
                  <Tag className="h-3 w-3" />
                  {item.categoryName}
                </span>
              ) : (
                <span className="text-[11px]">Uncategorized</span>
              )}
              {item.unit && (
                <span className="text-[11px] text-muted-foreground">
                  · {item.unitValue ? `${item.unitValue} ` : ""}{item.unit}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "sku",
      header: "SKU / Code",
      accessorKey: "sku",
      sortable: true,
      mobileLabel: "SKU / Code",
      mobilePriority: 3,
      cell: (item) => (
        <div className="space-y-0.5">
          <Badge variant="outline" className="font-mono text-xs font-semibold bg-muted/50">
            {item.sku || "N/A"}
          </Badge>
          {item.barcode && (
            <p className="flex items-center gap-1 text-[11px] text-muted-foreground font-mono">
              <Barcode className="h-3 w-3" />
              {item.barcode}
            </p>
          )}
        </div>
      ),
    },
    {
      id: "price",
      header: "Price",
      accessorKey: "price",
      sortable: true,
      align: "right",
      mobileLabel: "Price",
      mobilePriority: 2,
      cell: (item) => {
        const pricing = getRolePricingDetails(item);
        return (
          <div className="text-right space-y-0.5">
            <div className="font-bold text-sm text-foreground">
              {formatInr(pricing.ownPrice)}
              <span className="text-[10px] font-normal text-muted-foreground ml-1">({pricing.tierTag})</span>
            </div>
            <div className="text-xs text-primary font-medium">
              Sell: {formatInr(pricing.sellPrice)}
            </div>
            {pricing.mrp > pricing.ownPrice && (
              <div className="text-[11px] text-muted-foreground line-through">
                MRP {formatInr(pricing.mrp)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "businessVolume",
      header: "BV",
      accessorKey: "businessVolume",
      sortable: true,
      align: "center",
      mobileLabel: "BV",
      mobilePriority: 4,
      cell: (item) => (
        <div className="flex flex-col items-center">
          <Badge variant="secondary" className="font-semibold text-xs bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20">
            {item.businessVolume} BV
          </Badge>
          <span className="text-[10px] text-muted-foreground mt-0.5">
            Total: {(item.businessVolume * item.quantity).toFixed(1)}
          </span>
        </div>
      ),
    },
    {
      id: "quantity",
      header: "Available Stock",
      accessorKey: "quantity",
      sortable: true,
      align: "center",
      mobileLabel: "Stock",
      mobilePriority: 2,
      cell: (item) => {
        const qty = item.quantity ?? 0;
        const threshold = item.lowStockThreshold || 5;
        const isOut = qty === 0;
        const isLow = qty > 0 && qty <= threshold;

        return (
          <div className="flex flex-col items-center gap-1">
            <span className="font-bold text-sm text-foreground tabular-nums">
              {qty} units
            </span>
            {isOut ? (
              <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20 text-[10px] font-semibold">
                Out of Stock
              </Badge>
            ) : isLow ? (
              <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20 text-[10px] font-semibold">
                Low Stock (≤{threshold})
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-[10px] font-semibold">
                In Stock
              </Badge>
            )}
            {item.stockQuantity > 0 && item.stockQuantity !== qty && (
              <span className="text-[10px] text-muted-foreground">
                Shelf: {item.stockQuantity}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      sortable: true,
      align: "center",
      mobileLabel: "Status",
      mobilePriority: 5,
      cell: (item) => {
        const isLive = item.isActive !== false;
        return (
          <Badge
            variant="outline"
            className={
              isLive
                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-xs"
                : "bg-muted text-muted-foreground border-border text-xs"
            }
          >
            {isLive ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      align: "center",
      mobilePriority: 6,
      cell: (item) => (
        <div className="flex items-center justify-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 px-2.5 text-xs font-medium"
            onClick={() => openDetails(item)}
            title="View Details"
          >
            <Eye className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">View Details</span>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 gap-1.5 px-2.5 text-xs text-primary hover:text-primary hover:bg-primary/10"
            asChild
          >
            <Link to={catalogueUrl}>
              <ShoppingCart className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Order</span>
            </Link>
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={`${desk.role} Stock Inventory`}
        description={desk.stockNote}
        actions={
          <Button asChild className="gap-2 shadow-sm">
            <Link to={catalogueUrl}>
              <ShoppingCart className="h-4 w-4" />
              Order Stock from Catalogue
            </Link>
          </Button>
        }
      />

      {/* Summary Stat Cards */}
      <StatsGrid columns={4}>
        <CompactStatCard
          title="Total Inventory Units"
          value={stats.totalUnits.toLocaleString("en-IN")}
          icon={Boxes}
          className="bg-card"
        />
        <CompactStatCard
          title="Stock Product Lines"
          value={stats.totalItems}
          icon={Package}
          className="bg-card"
        />
        <CompactStatCard
          title="Total Stock Value"
          value={formatInr(stats.totalStockValue)}
          icon={IndianRupee}
          className="bg-card"
        />
        <CompactStatCard
          title="Total Business Volume"
          value={`${stats.totalBv.toFixed(1)} BV`}
          icon={Layers}
          className="bg-card"
        />
      </StatsGrid>

      {/* Stock Search and Filter Section */}
      <Card className="border border-border/80 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <form onSubmit={handleSearchSubmit} className="space-y-3">
            <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
              {/* Search Field */}
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search Product by Name, SKU, Code or Category..."
                  className="pl-10 h-10 w-full bg-background"
                />
              </div>

              {/* Action Buttons for Search */}
              <div className="flex items-center gap-2 shrink-0">
                <Button type="submit" className="h-10 px-4 gap-2">
                  <Search className="h-4 w-4" />
                  Search
                </Button>
                {isFiltered && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResetFilters}
                    className="h-10 px-3 gap-1.5 text-muted-foreground hover:text-foreground"
                    title="Reset all filters"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Reset
                  </Button>
                )}
              </div>

              {/* Filter Dropdowns */}
              <div className="flex flex-col sm:flex-row gap-2.5 shrink-0">
                {/* Category Filter */}
                <div className="w-full sm:w-[170px]">
                  <Select
                    value={categoryFilter}
                    onValueChange={(val) => setCategoryFilter(val)}
                  >
                    <SelectTrigger className="h-10 bg-background text-sm">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {availableCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Stock Status Filter */}
                <div className="w-full sm:w-[150px]">
                  <Select
                    value={stockStatusFilter}
                    onValueChange={(val) => setStockStatusFilter(val)}
                  >
                    <SelectTrigger className="h-10 bg-background text-sm">
                      <SelectValue placeholder="Stock Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Stock</SelectItem>
                      <SelectItem value="in_stock">In Stock (&gt;0)</SelectItem>
                      <SelectItem value="low_stock">Low Stock</SelectItem>
                      <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Active Filters Pill Bar */}
            {isFiltered && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Active Filters:</span>
                {appliedSearch && (
                  <Badge variant="secondary" className="gap-1 font-normal py-0.5">
                    Search: &ldquo;{appliedSearch}&rdquo;
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput("");
                        setAppliedSearch("");
                      }}
                      className="ml-1 hover:text-foreground"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {categoryFilter !== "all" && (
                  <Badge variant="secondary" className="gap-1 font-normal py-0.5">
                    Category: {categoryFilter}
                    <button
                      type="button"
                      onClick={() => setCategoryFilter("all")}
                      className="ml-1 hover:text-foreground"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {stockStatusFilter !== "all" && (
                  <Badge variant="secondary" className="gap-1 font-normal py-0.5">
                    Status: {stockStatusFilter.replace("_", " ")}
                    <button
                      type="button"
                      onClick={() => setStockStatusFilter("all")}
                      className="ml-1 hover:text-foreground"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetFilters}
                  className="h-6 px-2 text-xs text-primary hover:text-primary"
                >
                  Clear all
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Stock Table Component */}
      <Card className="border border-border/80 shadow-sm overflow-hidden">
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base font-bold text-foreground">
                Your Allocated Stock · {stats.totalUnits} Units
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Channel inventory credited to your account. Prices and BV are calculated per approved stock rules.
              </CardDescription>
            </div>
            {stats.lowStockCount > 0 && (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 gap-1 w-fit">
                <AlertTriangle className="h-3 w-3" />
                {stats.lowStockCount} items low in stock
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable<ChannelStockMine>
            columns={columns}
            data={pagedRows}
            getRowId={(row) => String(row.productId)}
            loading={stockQuery.isLoading}
            pagination={paginationMeta}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            emptyMessage={
              isFiltered
                ? "No products matched your search or filter criteria. Try clearing filters."
                : desk.stockEmpty
            }
            viewMode="responsive"
            cardClassName="border-0 shadow-none rounded-none"
            pageSizeOptions={[10, 20, 50]}
          />
        </CardContent>
      </Card>

      {/* Product Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto p-6 rounded-2xl">
          {selectedProduct && (
            <div className="space-y-6">
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-xs font-semibold">
                    {desk.role} Stock
                  </Badge>
                  <Badge
                    variant="outline"
                    className={
                      selectedProduct.isActive !== false
                        ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs"
                        : "bg-muted text-muted-foreground border-border text-xs"
                    }
                  >
                    {selectedProduct.status || "Active"}
                  </Badge>
                </div>
                <DialogTitle className="text-xl font-bold font-heading mt-2">
                  {selectedProduct.productName}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Complete product specifications and channel inventory breakdown.
                </DialogDescription>
              </DialogHeader>

              {/* Hero Image & Primary Details */}
              <div className="flex flex-col sm:flex-row gap-4 p-4 rounded-xl border bg-muted/20 items-center sm:items-start">
                <StandardImage
                  src={selectedProduct.imageUrl}
                  alt={selectedProduct.productName}
                  standard="product"
                  className="h-28 w-28 rounded-xl border bg-white shrink-0 object-contain shadow-sm"
                />
                <div className="flex-1 space-y-2 text-center sm:text-left">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-foreground">
                      {selectedProduct.productName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Category: {selectedProduct.categoryName || "General"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 pt-1">
                    <Badge variant="outline" className="font-mono text-xs">
                      SKU: {selectedProduct.sku || "N/A"}
                    </Badge>
                    {selectedProduct.barcode && (
                      <Badge variant="secondary" className="font-mono text-xs gap-1">
                        <Barcode className="h-3 w-3" />
                        {selectedProduct.barcode}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Role-Based Pricing Breakdown */}
              {(() => {
                const pricing = getRolePricingDetails(selectedProduct);
                return (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-foreground flex items-center gap-1.5">
                        <IndianRupee className="h-4 w-4 text-primary" />
                        {pricing.roleTitle}
                      </h4>
                      <Badge variant="outline" className="text-[11px] font-semibold bg-primary/10 text-primary border-primary/20">
                        {pricing.roleBadge}
                      </Badge>
                    </div>

                    {/* Primary Role Prices: Own Buy Price & Onward Selling Price */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* 1. Own Purchase Price */}
                      <div className="p-3.5 rounded-xl border border-primary/25 bg-primary/5 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-primary">{pricing.ownPriceLabel}</span>
                          <Badge variant="secondary" className="text-[10px] py-0 px-1.5">Your Cost</Badge>
                        </div>
                        <p className="text-2xl font-bold font-heading text-foreground">
                          {formatInr(pricing.ownPrice)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">{pricing.ownPriceSubtext}</p>
                      </div>

                      {/* 2. Onward Selling Price */}
                      <div className="p-3.5 rounded-xl border border-emerald-500/25 bg-emerald-500/5 dark:bg-emerald-950/20 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">{pricing.sellPriceLabel}</span>
                          <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30">Your Selling Rate</Badge>
                        </div>
                        <p className="text-2xl font-bold font-heading text-emerald-700 dark:text-emerald-400">
                          {formatInr(pricing.sellPrice)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">{pricing.sellPriceSubtext}</p>
                      </div>
                    </div>

                    {/* Secondary Details: Margin, MRP, BV */}
                    <div className="grid grid-cols-3 gap-2.5 text-xs">
                      <div className="p-3 rounded-lg border bg-card space-y-0.5">
                        <span className="text-muted-foreground text-[11px]">Profit Margin</span>
                        <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                          {pricing.margin > 0 ? (
                            <>+{formatInr(pricing.margin)} <span className="text-[10px] text-muted-foreground font-normal">({pricing.marginPct}%)</span></>
                          ) : (
                            <span className="text-muted-foreground">Standard</span>
                          )}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg border bg-card space-y-0.5">
                        <span className="text-muted-foreground text-[11px]">MRP</span>
                        <p className="text-sm font-bold text-foreground">
                          {formatInr(pricing.mrp)}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg border bg-card space-y-0.5">
                        <span className="text-muted-foreground text-[11px]">Business Volume</span>
                        <p className="text-sm font-bold text-amber-600 dark:text-amber-400">
                          {pricing.bv} BV
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Stock Levels & Unit Info */}
              <div className="space-y-2">
                <h4 className="text-sm font-bold text-foreground flex items-center gap-1.5">
                  <Boxes className="h-4 w-4 text-primary" />
                  Inventory Status
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-xs">
                  <div className="p-3 rounded-lg border bg-card space-y-1">
                    <span className="text-muted-foreground">Available Channel Stock</span>
                    <p className="text-base font-bold text-foreground">
                      {selectedProduct.quantity} units
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border bg-card space-y-1">
                    <span className="text-muted-foreground">Low Stock Threshold</span>
                    <p className="text-base font-bold text-foreground">
                      {selectedProduct.lowStockThreshold || 5} units
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border bg-card space-y-1">
                    <span className="text-muted-foreground">Unit Package</span>
                    <p className="text-base font-bold text-foreground">
                      {selectedProduct.unitValue ? `${selectedProduct.unitValue} ` : ""}
                      {selectedProduct.unit || "piece"}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border bg-card space-y-1">
                    <span className="text-muted-foreground">Stock Value (at Buy Price)</span>
                    <p className="text-base font-bold text-foreground">
                      {formatInr(getRolePricingDetails(selectedProduct).stockBuyValue)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border bg-card space-y-1">
                    <span className="text-muted-foreground">Stock Value (at Sell Price)</span>
                    <p className="text-base font-bold text-foreground">
                      {formatInr(getRolePricingDetails(selectedProduct).stockSellValue)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border bg-card space-y-1">
                    <span className="text-muted-foreground">Total BV Value</span>
                    <p className="text-base font-bold text-amber-600 dark:text-amber-400">
                      {(selectedProduct.businessVolume * selectedProduct.quantity).toFixed(1)} BV
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border bg-card space-y-1 sm:col-span-3">
                    <span className="text-muted-foreground">Stock Health</span>
                    <div className="pt-0.5">
                      {selectedProduct.quantity === 0 ? (
                        <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20 text-xs">
                          Out of Stock
                        </Badge>
                      ) : selectedProduct.quantity <= (selectedProduct.lowStockThreshold || 5) ? (
                        <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20 text-xs">
                          Low Stock
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-xs">
                          In Stock
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-end gap-2.5 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsDetailOpen(false)}
                  className="w-full sm:w-auto"
                >
                  Close
                </Button>
                <Button asChild className="w-full sm:w-auto gap-2">
                  <Link to={catalogueUrl} onClick={() => setIsDetailOpen(false)}>
                    <ShoppingCart className="h-4 w-4" />
                    Order More Stock
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
